import { useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useGetModalMetaData,
  useIsPlainModalOpenSelector,
} from '../../../store/modules/modals/modals.selector';
import {
  closeModalByNameAction,
  PlainModals,
} from '../../../store/modules/modals/modals.reducer';
import { ModalContainer } from '../../../components/modals/modal-container/modal-container';
import { ApiContext } from '../../../components/providers/api-provider/api-provider';
import { Box, Typography } from '@mui/material';
import { StyledCheckbox } from 'components/multi-filter/styles';
import { ContainerWithLoader } from 'components/container-with-loader/container-with-loader';
import { setCompliancePolicies } from 'store/modules/compliance/compliance.reducer';
import { useGetComplianceSectionData } from 'store/modules/compliance/compliance.selector';
import { TableComponent } from 'components/table/table-component/table-component';
import { compliancePoliciesTableColumns } from '../constants';
import { SeverityChip } from 'pages/policies/components/severity-chip/severity-chip';
import { StyledTooltip } from 'components/tooltip/tooltip';
import { Truncate } from 'components/truncate/truncate';
import { Colors } from 'constants/colors';
import { useNavigate } from 'react-router-dom';
import { PolicyTags } from 'pages/policies/components/policy-tags/policy-tags';
import { showAllTagName } from 'pages/policies/constant';

export const CompliancePoliciesModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const metaData = useGetModalMetaData();
  const apiContext = useContext(ApiContext);
  const [selectedTag, setSelectedTag] = useState(showAllTagName);

  const { sectionIndex } = metaData;

  const complianceSectionData = useGetComplianceSectionData(sectionIndex);

  const isPolicyModalOpen = useIsPlainModalOpenSelector(
    PlainModals.CUSTOM_COMPLIANCE_POLICIES_MODAL,
  );

  const { data, isLoading, isError } = apiContext.baseApi?.useGetPoliciesQuery(
    {},
  );

  const filteredData = useMemo(
    () =>
      Array.isArray(data)
        ? data.filter((policy) => {
            return (
              policy?.policy_type !== 'CUSTOM' &&
              (selectedTag === showAllTagName ||
                policy?.tags?.includes(selectedTag))
            );
          })
        : [],
    [data, selectedTag],
  );

  const handleCloseModal = () => {
    dispatch(
      closeModalByNameAction(PlainModals.CUSTOM_COMPLIANCE_POLICIES_MODAL),
    );
  };

  const [selectedPolicies, setSelectedPolicies] = useState(
    complianceSectionData.selectedPolicies,
  );

  const handleSelectPolicy = (index: number, value: boolean) => {
    const policyName = filteredData?.[index].name;
    if (!policyName) return;
    const positionIndex = selectedPolicies.findIndex(
      (row) => row.name === policyName,
    );
    if (positionIndex !== -1) {
      setSelectedPolicies((prev) => [
        ...prev.slice(0, positionIndex),
        ...prev.slice(positionIndex + 1),
      ]);
    } else {
      setSelectedPolicies((prev) => [
        ...prev,
        { name: policyName, position: -1 },
      ]);
    }
  };

  const handleConfirmPolicyAddition = () => {
    let current = 0;
    const policies = selectedPolicies.map((row) => {
      const obj = { name: row.name, position: current };
      current++;
      return obj;
    });
    dispatch(
      setCompliancePolicies({
        index: metaData?.sectionIndex,
        selectedPolicies: policies,
      }),
    );
    handleCloseModal();
  };

  const renderCheckBox = (policy: any) => {
    return (
      <Box>
        <StyledCheckbox
          checked={
            selectedPolicies.findIndex((row) => row?.name === policy?.name) !==
            -1
          }
          onChange={(_, checked) =>
            handleSelectPolicy(policy.listItemIndex, checked)
          }
          sx={{ px: 2 }}
        />
      </Box>
    );
  };

  const renderSeverityCell = (policy: any) => (
    <SeverityChip severityScore={policy.severity} />
  );

  const RenderNameCell = (policy: any) => {
    const editPolicy = (policyName: string) => {
      navigate(
        `/policy?policy=${policyName}&policyType=${policy?.policy_type}`,
      );
      handleCloseModal();
    };
    return (
      <Box sx={{ marginRight: '10px' }}>
        <StyledTooltip title={policy.description} placement="top-start">
          <Typography
            sx={{
              color: Colors.binge,
              marginBottom: '9px',
              cursor: 'pointer',
            }}
            variant="body1"
            onClick={() => editPolicy(policy?.name)}
          >
            <Truncate lines={1} trimWhitespace width={600}>
              {policy.name}
            </Truncate>
          </Typography>
        </StyledTooltip>
      </Box>
    );
  };

  const extraCellsRenderObj = {
    severity: renderSeverityCell,
    checkbox: renderCheckBox,
    name: RenderNameCell,
  };

  return (
    <ModalContainer
      isOpenModal={isPolicyModalOpen}
      title="Add Compliance Policies"
      handleCloseModal={handleCloseModal}
      modalWidth="1200px"
      handleConfirm={handleConfirmPolicyAddition}
      top={'5%'}
      centerX
      modalHeight={'100%'}
    >
      <ContainerWithLoader isLoading={isLoading} isError={isError}>
        <PolicyTags
          policyData={data?.filter(
            (policy: any) => policy?.policy_type !== 'CUSTOM',
          )}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
        />
        <TableComponent
          tableId={''}
          isListLoading={isLoading}
          isListErrored={isError}
          isEmptyList={false}
          isListFetching={isLoading}
          list={filteredData}
          extraCellsRenderObj={extraCellsRenderObj}
          tableColumns={compliancePoliciesTableColumns}
          notPaginated
          tableHeight="400px"
          tableMinHeight="400px"
        />
      </ContainerWithLoader>
    </ModalContainer>
  );
};
